import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Loader2, Plus, Trash2, Send, CheckCircle2 } from "lucide-react";

const ROLES = { staff: "Staff", fotografo: "Fotografo", agenzia: "Agenzia", altro: "Altro" };

export default function InviaReferto({ module, title, scope, open, onOpenChange }) {
  const [contacts, setContacts] = useState([]);
  const [log, setLog] = useState([]);
  const [sel, setSel] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "staff" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try { const d = await api.contacts(scope.tenant, scope.hotel); setContacts(d.contacts); setLog(d.log.filter((l) => l.module === module)); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setAdding(true);
    try {
      const c = await api.addContact({ tenant: scope.tenant, hotel: scope.hotel, ...form });
      setContacts((cs) => [...cs, c]); setSel((s) => [...s, c.id]); setForm({ name: "", email: "", role: "staff" });
      toast.success(`${c.name} aggiunto alla rubrica.`);
    } catch (e) { toast.error(e.message); } finally { setAdding(false); }
  };
  const remove = async (id) => {
    try { await api.removeContact({ tenant: scope.tenant, hotel: scope.hotel, contact_id: id }); setContacts((cs) => cs.filter((c) => c.id !== id)); setSel((s) => s.filter((x) => x !== id)); }
    catch (e) { toast.error(e.message); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.shareReport({ tenant: scope.tenant, hotel: scope.hotel, module, contact_ids: sel, note });
      toast.success(`Referto inviato a ${r.recipients.length} destinatar${r.recipients.length === 1 ? "io" : "i"}.`);
      setSel([]); setNote(""); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto leutelia-scroll bg-brand-cream" data-testid={`share-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink flex items-center gap-2"><Mail size={20} className="text-brand-gold" /> Invia il referto {title}</DialogTitle>
          <DialogDescription>Ogni destinatario riceve un'email LEUTELIA con la sintesi e un link per scaricare il PDF completo (valido 14 giorni).</DialogDescription>
        </DialogHeader>

        <div className="bg-white rounded-xl border border-brand-gold/25 p-4">
          <div className="text-[12px] font-bold uppercase tracking-wide text-brand-muted mb-2">Destinatari</div>
          {contacts.length === 0 && <p className="text-[13px] text-brand-muted italic" data-testid="share-no-contacts">Nessun contatto in rubrica: aggiungi staff, fotografo o agenzia qui sotto.</p>}
          <div className="space-y-1.5">
            {contacts.map((c) => (
              <label key={c.id} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${sel.includes(c.id) ? "border-brand-green bg-brand-green/5" : "border-brand-gold/20"}`} data-testid={`share-contact-${c.id}`}>
                <input type="checkbox" checked={sel.includes(c.id)} onChange={(e) => setSel((s) => e.target.checked ? [...s, c.id] : s.filter((x) => x !== c.id))} className="accent-[#16493A]" data-testid={`share-contact-check-${c.id}`} />
                <div className="min-w-0 flex-1"><div className="text-[13.5px] text-brand-ink font-medium">{c.name} <span className="ml-1 px-1.5 py-0.5 rounded-full bg-brand-cream border border-brand-gold/30 text-[10.5px] text-brand-green font-semibold">{ROLES[c.role] || c.role}</span></div><div className="text-[12px] text-brand-muted truncate">{c.email}</div></div>
                <button onClick={(e) => { e.preventDefault(); remove(c.id); }} className="text-brand-muted hover:text-brand-maroon" aria-label="Rimuovi" data-testid={`share-contact-remove-${c.id}`}><Trash2 size={15} /></button>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.3fr_120px_auto] gap-2 mt-3">
            <Input placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-[13px] bg-white" data-testid="share-new-name" />
            <Input placeholder="email@esempio.it" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 text-[13px] bg-white" data-testid="share-new-email" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-9 text-[13px] bg-white" data-testid="share-new-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={add} disabled={adding || !form.name.trim() || !form.email.trim()} variant="outline" className="h-9 border-brand-green/30 text-brand-green text-[12.5px] font-semibold" data-testid="share-new-add">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} className="mr-1" />} Aggiungi
            </Button>
          </div>
        </div>

        <label className="block text-[12px] font-bold uppercase tracking-wide text-brand-muted">Messaggio (opzionale)
          <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} placeholder="Es. Da leggere prima della riunione di lunedì." className="mt-1 bg-white text-[13px]" rows={2} data-testid="share-note" />
        </label>

        {log.length > 0 && (
          <div className="text-[12px] text-brand-muted" data-testid="share-log">
            <div className="font-bold uppercase tracking-wide mb-1">Invii precedenti</div>
            {log.slice(0, 4).map((l, i) => <div key={i} className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-700" />{new Date(l.sent_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {l.recipients.map((r) => r.name).join(", ")}</div>)}
          </div>
        )}

        <DialogFooter>
          <Button onClick={send} disabled={busy || !sel.length} className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold" data-testid="share-send">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} Invia a {sel.length || "…"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
